import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Wordmark } from "../components/Brand";
import { apiError } from "../lib/api";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invite/${token}`).then((r) => setInvite(r.data)).catch((e) => setError(apiError(e, "Invalid invitation link.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/invite/${token}/accept`, { password });
      setDone(true);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="mb-8"><Wordmark size="text-2xl" />
          <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#78716C]">Operations Hub — Consultant setup</div></div>
        <div className="card p-8 space-y-5 fade-up">
          {done ? (
            <div className="text-center space-y-4" data-testid="invite-success">
              <CheckCircle2 size={36} className="mx-auto text-[#4A7C59]" />
              <h1 className="font-serif font-bold text-xl text-navy">Your account is ready</h1>
              <p className="text-sm text-[#57534E]">Sign in with your CKingEducation email and the password you just set.</p>
              <Link to="/login" className="btn-primary w-full" data-testid="invite-goto-login">Go to sign in</Link>
            </div>
          ) : invite ? (
            <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
              <div>
                <h1 className="font-serif font-bold text-xl text-navy">Welcome, {invite.name}</h1>
                <p className="text-sm text-[#57534E] mt-1">Set a password for <b>{invite.email}</b>. Minimum 10 characters.</p>
              </div>
              <div><label className="label">New password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} className="input w-full !pr-11" value={password} onChange={(e) => setPassword(e.target.value)}
                    required minLength={10} data-testid="invite-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="invite-password-toggle"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-navy transition-colors"
                    title={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div></div>
              <div><label className="label">Confirm password</label>
                <input type={showPassword ? "text" : "password"} className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required minLength={10} data-testid="invite-confirm-input" /></div>
              {error && <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]" data-testid="invite-error">{error}</div>}
              <button className="btn-primary w-full" disabled={busy} data-testid="invite-submit-button">{busy ? "Setting up…" : "Set password & activate"}</button>
            </form>
          ) : (
            <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]" data-testid="invite-invalid">
              {error || "Loading…"}</div>
          )}
        </div>
      </div>
    </div>
  );
}
